import { useMemo } from 'react'
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table'

import Flex from '@/elements/Flex'
import Text from '@/elements/Text'
import { defaultColumnOptions, type TableAny } from '@/utils/tables'
import type { DeviceTokenResponse } from '@/api/types/DeviceToken'
import dayjs from 'dayjs'
import Button from '@/elements/Button'
import { Trash2 } from 'lucide-react'

interface DeviceTokensTableType {
  id: DeviceTokenResponse['id']
  deviceName: DeviceTokenResponse['deviceName']
  createdAt?: DeviceTokenResponse['createdAt']
  isCurrentDevice?: boolean
}

const columnHelper = createColumnHelper<DeviceTokensTableType>()

export function useDeviceTokensTable(
  deviceTokens: DeviceTokenResponse[],
  myDeviceToken: string | undefined,
  onRemoveDeviceToken: (id: string) => void
) {
  const data: DeviceTokensTableType[] = useMemo(() => (
    deviceTokens ? deviceTokens.map(row => ({
      id: row?.id,
      deviceName: row?.deviceName || '',
      createdAt: row?.createdAt,
      isCurrentDevice: myDeviceToken
        ? row.token === myDeviceToken
        : false,
    })) : []
  ), [deviceTokens, myDeviceToken])

  const columns: ColumnDef<DeviceTokensTableType, TableAny>[] = [
    columnHelper.accessor('deviceName', {
      ...defaultColumnOptions,
      header: 'Name',
      enableSorting: false,
      cell: info => {
        const isCurrentDevice = info.row.original.isCurrentDevice

        return (
          <Flex className='user-cell'>
            <Text variant='textblock'>
              {info.getValue() as string}
            </Text>
            {isCurrentDevice && (
              <Text color='green' variant='footnote'>
                This device
              </Text>
            )}
          </Flex>
        )
      },
    }),
    columnHelper.accessor('createdAt', {
      ...defaultColumnOptions(),
      header: 'Added On',
      enableSorting: false,
      cell: info => {
        const createdAt = info.getValue()
        const renderDate = (date: string) => dayjs(date).format('L LT')
        return createdAt ? renderDate(createdAt) : 'N/A'
      }
    }),
    columnHelper.display({
      id: 'actions',
      ...defaultColumnOptions(),
      header: 'Actions',
      meta: { width: 100 },
      enableSorting: false,
      cell: info => {
        const deviceTokenId = info.row.original.id
        return (
          <Flex justify='center' align='center' className='actions-cell'>
            <Button
              small
              variant='iconDangerBtn'
              onClick={() => onRemoveDeviceToken(deviceTokenId)}
            >
              <Trash2 size={18} style={{ minWidth: 18 }} />
            </Button>
          </Flex>
        )
      }
    }),
  ]

  return { data, columns }
}
